import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { Box, Typography } from '@mui/material';

import { useClickAnnouncementMutation, useGetActiveAnnouncementsQuery } from 'api/announcements-api';
import { CampaignModal } from 'shared/Modals/CampaignModal';
import { userSelector } from 'store/slices/user-slice';
import { ActiveAnnouncement } from 'types/api/api-announcements.types';

// Operator announcements dialog: shows the first active, not-yet-dismissed announcement for the
// logged-in customer, one at a time. Dismissal is remembered per announcement in localStorage
// (no server state); clicking "Learn more" counts a click server-side and opens the link.
const DISMISSED_KEY = 'dismissedAnnouncements';

const readDismissed = (): string[] => {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(parsed) ? (parsed as string[]) : [];
  } catch {
    return [];
  }
};

const storeDismissed = (ids: string[]) => {
  try {
    localStorage.setItem(DISMISSED_KEY, JSON.stringify(ids));
  } catch {
    // localStorage unavailable (private mode) — the dialog just reappears next session.
  }
};

export const AnnouncementsModal = () => {
  const idToken = useSelector(userSelector).user?.idToken;
  const isIdTokenReady = !!idToken?.__raw;

  const { data } = useGetActiveAnnouncementsQuery(undefined, { skip: !isIdTokenReady });
  const [clickAnnouncement] = useClickAnnouncementMutation();
  const [dismissed, setDismissed] = useState<string[]>(readDismissed);

  const current: ActiveAnnouncement | undefined = useMemo(
    () => (data?.announcements ?? []).find(a => !dismissed.includes(a.id)),
    [data, dismissed],
  );

  const dismiss = useCallback(
    (id: string) => {
      const next = [...dismissed, id];
      setDismissed(next);
      storeDismissed(next);
    },
    [dismissed],
  );

  if (!current) return null;

  const handleClose = () => dismiss(current.id);

  const handlePrimary = () => {
    if (current.linkUrl) {
      clickAnnouncement(current.id).catch(() => {
        // Best-effort metric — never block the navigation on it.
      });
      window.open(current.linkUrl, '_blank', 'noopener,noreferrer');
    }
    dismiss(current.id);
  };

  return (
    <CampaignModal
      title={current.title}
      content={
        <Box>
          <Typography component='div' variant='body1' style={{ whiteSpace: 'pre-line' }}>
            {current.message}
          </Typography>
        </Box>
      }
      primaryButtonText={current.linkUrl ? 'Learn more' : 'Got it'}
      secondaryButtonText={current.linkUrl ? 'Dismiss' : undefined}
      onClose={handleClose}
      primaryButtonHandler={handlePrimary}
      secondaryButtonHandler={handleClose}
      showCloseIcon
    />
  );
};
